import { useCallback, useEffect, useMemo, useState } from "react";
import { notificationStorage } from "services/notification-storage";

export type MessagesPanelState = 'minimized' | 'standard' | 'maximized';
export type MaximizedPanel = 'messages' | 'notes' | null;

interface UseMessagesPanelProps {
  featureEnabled: boolean;
  orderId?: string;
  currentUser?: string;
  lastComment?: { commentId: string; postedBy: string } | null;
}

export interface UseMessagesPanelReturn {
  panelState: MessagesPanelState;
  maximizedPanel: MaximizedPanel;
  showBadge: boolean;
  minimize: () => void;
  restore: () => void;
  maximize: (panel: 'messages' | 'notes') => void;
  closeMaximized: () => void;
  isMinimized: boolean;
  isMaximized: boolean;
  isStandard: boolean;
}

const NOOP = () => { /* no-op for disabled state */ };

export const useMessagesPanel = ({ featureEnabled, orderId, currentUser, lastComment }: UseMessagesPanelProps): UseMessagesPanelReturn => {
  const [panelState, setPanelState] = useState<MessagesPanelState>('minimized');
  const [maximizedPanel, setMaximizedPanel] = useState<MaximizedPanel>(null);
  const [lastSeenCommentId, setLastSeenCommentId] = useState<string | null>(
    () => orderId ? notificationStorage.getLastSeenCommentId(orderId) : null
  );

  useEffect(() => {
    if (orderId) {
      setLastSeenCommentId(notificationStorage.getLastSeenCommentId(orderId));
    }
  }, [orderId]);

  const showBadge = useMemo(() => {
    if (!featureEnabled || !orderId || !lastComment?.commentId) return false;
    if (lastComment.postedBy === currentUser) return false;

    if (!lastSeenCommentId) return true;
    return lastComment.commentId !== lastSeenCommentId;
  }, [featureEnabled, orderId, currentUser, lastComment, lastSeenCommentId]);

  useEffect(() => {
    if (!featureEnabled || !orderId || !lastComment?.commentId) return;
    if (panelState !== 'minimized') {
      notificationStorage.setLastSeenCommentId(orderId, lastComment.commentId);
      setLastSeenCommentId(lastComment.commentId);
    }
  }, [panelState, lastComment, featureEnabled, orderId]);

  const minimize = useCallback(() => {
    setPanelState('minimized');
    setMaximizedPanel(null);
  }, []);

  const restore = useCallback(() => {
    setPanelState('standard');
    setMaximizedPanel(null);
  }, []);

  const maximize = useCallback((panel: 'messages' | 'notes') => {
    setPanelState('maximized');
    setMaximizedPanel(panel);
  }, []);

  const closeMaximized = useCallback(() => {
    setPanelState('standard');
    setMaximizedPanel(null);
  }, []);

  const derived = useMemo(() => ({
    isMinimized: panelState === 'minimized',
    isMaximized: panelState === 'maximized',
    isStandard: panelState === 'standard',
  }), [panelState]);

  if (!featureEnabled) {
    return {
      panelState: 'standard',
      maximizedPanel: null,
      showBadge: false,
      minimize: NOOP,
      restore: NOOP,
      maximize: NOOP,
      closeMaximized: NOOP,
      isMinimized: false,
      isMaximized: false,
      isStandard: true,
    };
  }

  return {
    panelState,
    maximizedPanel,
    showBadge,
    minimize,
    restore,
    maximize,
    closeMaximized,
    ...derived,
  };
};
